import { Col, DatePicker, Form, InputNumber, Select, Switch } from 'antd';
import { FormInstance } from 'antd/es/form/Form';
import moment, { Moment } from 'moment';
import { useCallback, useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';
import ShippingCompanySelect from '~/components/atoms/Inputs/ShippingCompanySelect';
import { GFPContext } from '~/context/GFPContext';
import { globalContext } from '~/context/GlobalContext';
import { brFormat } from '~/helpers/util';
import { isKlokCommercialDateDisabled } from '~/utils/date';
import useGfpStore from '~/zustand/gfp/gfp-service-state';
import { FiltersTableRow } from './style';

interface Props {
  form: FormInstance<any>;
}

export default function FormAddKlokGFP(props: Props) {
  const { form } = props;
  const { t } = useTranslation();

  const { siteIdSelected } = useContext(globalContext);
  const { outDate, setOutDate } = useContext(GFPContext);

  const {
    klokReservationsSelected,
    stocksSelected,
    setStocks,
    setKlokReservationTable,
    setKlokReservationsSelected,
    setStocksSelected,
    getAllCampaigns,
  } = useGfpStore(
    useShallow(state => ({
      klokReservationsSelected: state.klokReservationsSelected,
      stocksSelected: state.stocksSelected,
      setStocks: state.setStocks,
      setKlokReservationTable: state.setKlokReservationTable,
      setKlokReservationsSelected: state.setKlokReservationsSelected,
      setStocksSelected: state.setStocksSelected,
      getAllCampaigns: state.getAllCampaigns,
    })),
  );

  function disabledDate(current: Moment) {
    return current && current < moment().startOf('day');
  }

  const disableComercialDate = useCallback(
    (value: Moment) => isKlokCommercialDateDisabled(value, form.getFieldValue('outDate')),
    [form],
  );

  useEffect(() => {
    if (outDate === null) {
      setOutDate(form.getFieldValue('outDate'));
    }
  }, []);

  const handleChangeOutDate = (date: Moment | null) => {
    form.setFieldValue('commercialDate', null);
    setStocks([]);
    setKlokReservationTable([]);
    setStocksSelected([]);
    setKlokReservationsSelected([]);

    if (date) {
      getAllCampaigns(date.format('YYYY-MM-DD'));
    }
  };

  const handleChangeCommercialDate = () => {
    setStocks([]);
    setKlokReservationTable([]);
    setStocksSelected([]);
    setKlokReservationsSelected([]);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const { key } = e;

    if (
      !/^[0-9]$/.test(key) &&
      key !== 'Backspace' &&
      key !== 'Delete' &&
      key !== 'ArrowLeft' &&
      key !== 'ArrowRight' &&
      key !== 'Tab'
    ) {
      e.preventDefault();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasteData = e.clipboardData.getData('Text');
    if (!/^[0-9]+$/.test(pasteData)) {
      e.preventDefault();
    }
  };

  const isSwitchDisabled = useMemo(() => {
    return klokReservationsSelected.length > 0 || stocksSelected.length > 0;
  }, [klokReservationsSelected, stocksSelected]);

  return (
    <>
      <FiltersTableRow justify="space-between">
        <Col lg={6}>
          <Form.Item label={t('gfp.modal.addGfp.form.emissionDate')} name="emissionDate">
            <DatePicker
              picker="date"
              placeholder={t('generals.selectPlaceholder')}
              format={brFormat}
              style={{ width: '100%' }}
              disabled
              key={23424}
            />
          </Form.Item>
        </Col>
        <Col lg={6}>
          <Form.Item
            label={t('gfp.modal.addGfp.form.outDate')}
            name="outDate"
            rules={[{ required: true, message: 'Selecione a data' }]}
          >
            <DatePicker
              picker="date"
              placeholder={t('generals.selectPlaceholder')}
              format={brFormat}
              style={{ width: '100%' }}
              key={23425}
              onBlur={date => setOutDate(moment(date.target.value))}
              disabledDate={disabledDate}
              onChange={handleChangeOutDate}
            />
          </Form.Item>
        </Col>
        <Col lg={6}>
          <Form.Item
            label={t('gfp.modal.addGfp.form.commercialDate')}
            name="commercialDate"
            rules={[{ required: true, message: 'Selecione a data' }]}
          >
            <DatePicker
              disabledDate={disableComercialDate}
              picker="date"
              allowClear
              placeholder={t('generals.selectPlaceholder')}
              format={brFormat}
              style={{ width: '100%' }}
              key={23428}
              onChange={handleChangeCommercialDate}
            />
          </Form.Item>
        </Col>
        <Col lg={5}>
          <Form.Item label={t('gfp.modal.addGfp.form.local')} name="pickupLocation">
            <Select disabled>
              <Select.Option key={1} value={1}>
                CVH
              </Select.Option>
            </Select>
          </Form.Item>
        </Col>
      </FiltersTableRow>
      <FiltersTableRow style={{ marginTop: '-15px', gap: '10px', justifyContent: 'left' }}>
        <Col>
          <Form.Item
            label={t('gfp.modal.addGfp.form.divider')}
            name="partitionQuantity"
            rules={[{ required: true, message: 'Insira uma divisória' }]}
          >
            <InputNumber
              min={0}
              onPaste={handlePaste}
              parser={value => (value ? parseInt(value.replace(/[^0-9]/g, ''), 10) : 0)}
              onKeyDown={handleKeyDown}
              style={{ marginLeft: '18px', width: '222px' }}
              type="text"
            />
          </Form.Item>
        </Col>
        <Col>
          <Form.Item
            label={t('gfp.modal.addGfp.form.multiply')}
            name="multiple"
            style={{ width: '316px' }}
          >
            <InputNumber
              min={0}
              max={30}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              parser={value => (value ? parseInt(value.replace(/[^0-9]/g, ''), 10) : 1)}
            />
          </Form.Item>
        </Col>
        <Col>
          <Form.Item
            label={t('gfp.modal.addGfp.form.transport')}
            name="transporterId"
            style={{ marginLeft: '13px' }}
            rules={[{ required: true, message: 'Insira uma transportadora' }]}
          >
            <ShippingCompanySelect
              form={form}
              siteId={siteIdSelected}
              style={{ width: '250px' }}
              formFieldName="transporterId"
            />
          </Form.Item>
        </Col>
        <Col>
          <Form.Item
            label="Utilizar Reserva KLOK?"
            name="klokReservationSwitch"
            style={{ marginLeft: '13px' }}
            valuePropName="checked"
          >
            <Switch disabled={isSwitchDisabled} />
          </Form.Item>
        </Col>
      </FiltersTableRow>
    </>
  );
}
